import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';

// Core components loaded immediately
import { AdminSidebar, TAB_TITLES } from '../components/admin/AdminSidebar';
import { AdminHeader } from '../components/admin/AdminHeader';
import { AdminMobileMenu } from '../components/admin/AdminMobileMenu';

// Lazy-loaded section components for code splitting
const MediaLibrary = lazy(() => import('./MediaLibrary'));
const MediaPicker = lazy(() => import('../components/MediaPicker'));
const AdminModals = lazy(() => import('../components/admin/AdminModals').then(m => ({ default: m.AdminModals })));
const MemberVaultSection = lazy(() => import('../components/admin/MemberVaultSection').then(m => ({ default: m.MemberVaultSection })));
const TeamSection = lazy(() => import('../components/admin/TeamSection').then(m => ({ default: m.TeamSection })));
const SeriesSection = lazy(() => import('../components/admin/SeriesSection').then(m => ({ default: m.SeriesSection })));
const GuestsSection = lazy(() => import('../components/admin/GuestsSection').then(m => ({ default: m.GuestsSection })));
const GiveawaysSection = lazy(() => import('../components/admin/GiveawaysSection').then(m => ({ default: m.GiveawaysSection })));
const HomepageLayoutSection = lazy(() => import('../components/admin/HomepageLayoutSection').then(m => ({ default: m.HomepageLayoutSection })));
const MembersSection = lazy(() => import('../components/admin/MembersSection').then(m => ({ default: m.MembersSection })));
const ClubhouseSection = lazy(() => import('../components/admin/ClubhouseSection').then(m => ({ default: m.ClubhouseSection })));
const SettingsSection = lazy(() => import('../components/admin/SettingsSection').then(m => ({ default: m.SettingsSection })));
const EmailTemplatesSection = lazy(() => import('../components/admin/EmailTemplatesSection').then(m => ({ default: m.EmailTemplatesSection })));
const EmailCampaignsV2Section = lazy(() => import('../components/admin/EmailCampaignsV2Section').then(m => ({ default: m.EmailCampaignsV2Section })));
const EpisodesSection = lazy(() => import('../components/admin/EpisodesSection').then(m => ({ default: m.EpisodesSection })));
const LinkHubSection = lazy(() => import('../components/admin/LinkHubSection').then(m => ({ default: m.LinkHubSection })));
const PagesSection = lazy(() => import('../components/admin/PagesSection').then(m => ({ default: m.PagesSection })));
const PageManagementV2 = lazy(() => import('../components/admin/PageManagementV2/index.jsx').then(m => ({ default: m.PageManagementV2 })));

// Lazy-loaded modals
const GiveawayEditorModal = lazy(() => import('../components/admin/GiveawayEditorModal').then(m => ({ default: m.GiveawayEditorModal })));
const EpisodeEditorModal = lazy(() => import('../components/admin/EpisodeEditorModal').then(m => ({ default: m.EpisodeEditorModal })));
const DeleteMemberModal = lazy(() => import('../components/admin/DeleteMemberModal').then(m => ({ default: m.DeleteMemberModal })));
const LinkHubEditorModal = lazy(() => import('../components/admin/LinkHubEditorModal').then(m => ({ default: m.LinkHubEditorModal })));

// Custom hooks for state management
import { useSeriesState } from '../hooks/admin/useSeriesState';
import { useGuestsState } from '../hooks/admin/useGuestsState';
import { useMemberVaultState } from '../hooks/admin/useMemberVaultState';
import { useEpisodesState } from '../hooks/admin/useEpisodesState';
import { useLinkHubState } from '../hooks/admin/useLinkHubState';
import { useGiveawaysState } from '../hooks/admin/useGiveawaysState';
import { useHomepageLayoutState } from '../hooks/admin/useHomepageLayoutState';
import { useEmailTemplatesState } from '../hooks/admin/useEmailTemplatesState';
import { useCampaignsV2State } from '../hooks/admin/useCampaignsV2State';
import { useClubhouseState } from '../hooks/admin/useClubhouseState';
import { useAdminTeamState } from '../hooks/admin/useAdminTeamState';
import { useMembersState } from '../hooks/admin/useMembersState';
import { useSettingsState } from '../hooks/admin/useSettingsState';
import { usePagesState } from '../hooks/admin/usePagesState';
import { useMediaPickerState } from '../hooks/admin/useMediaPickerState';

// Loading fallback component for lazy-loaded sections
const SectionLoadingFallback = () => (
  <div className="admin-loading" style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '200px',
    color: '#166534'
  }}>
    <RefreshCw className="animate-spin" size={24} style={{ marginRight: '8px' }} />
    Loading section...
  </div>
);

import { 
  Image as ImageIcon, 
  FileText, 
  Settings, 
  Mail, 
  Users, 
  UserPlus,
  Megaphone, 
  Download, 
  RefreshCw,
  BarChart3,
  Trophy,
  Gift,
  TrendingUp,
  Globe,
  Mic,
  FileSpreadsheet,
  AtSign,
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
  Podcast,
  Apple,
  Headphones,
  Code,
  FileCode,
  Tag,
  Info,
  Save,
  Edit2,
  Edit3,
  X,
  Menu,
  ArrowLeft,
  Video,
  Check,
  CheckCircle,
  Trash2,
  Link2,
  ExternalLink,
  Plus,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  GripVertical,
  AlertCircle,
  Layout,
  Palette,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  Send,
  Copy
} from 'lucide-react';
import './Admin.css';
import '../components/admin/PageManagementV2/PageManagementV2.css';
import { API_URL } from '../utils/apiUrl';
import RichTextEditor from '../components/RichTextEditor';
import { extractYouTubeId, formatDateWithoutOffset, formatFieldLabel, isMediaField } from '../components/admin/utils';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial tab from URL or default to 'pages'
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl || 'pages';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [adminUser, setAdminUser] = useState(null);
  
  // Pages CMS state - extracted to usePagesState hook
  const pagesState = usePagesState({ setError, setSuccess, setLoading });
  const {
    pages,
    selectedPage,
    setSelectedPage,
    fetchPages,
    fetchPageDetails,
    updatePageField,
    updateSectionField,
    updateBlockField,
    updateBlockContent,
  } = pagesState;
  
  // Site Settings state - extracted to useSettingsState hook
  const settingsState = useSettingsState({ setError, setSuccess, setLoading });
  const {
    siteSettings,
    selectedSettingsTab,
    setSiteSettings,
    setSelectedSettingsTab,
    fetchSiteSettings,
    handleSettingsChange,
    handleNestedSettingsChange,
    handleSaveSettings,
  } = settingsState;
  
  // Newsletter Members state - extracted to useMembersState hook
  const membersState = useMembersState({ setError, setSuccess });
  const {
    members,
    loadingMembers,
    fetchMembers,
    deleteMember,
    exportMembers,
  } = membersState;
  
  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPermissionLevel, setCurrentPermissionLevel] = useState('write');
  
  // Helper to switch tabs and clear errors
  const handleTabChange = (tab, permissionLevel = 'write') => {
    setActiveTab(tab);
    setCurrentPermissionLevel(permissionLevel);
    setSidebarOpen(false);
    setError(null); // Clear any errors when switching tabs
    
    // Update URL with the new tab
    setSearchParams({ tab });
  };
  
  // Sync tab from URL when browser back/forward is used
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);
  
  // Check if current tab is read-only
  const isReadOnly = currentPermissionLevel === 'read';
  
  // Team management state - extracted to useAdminTeamState hook
  const adminTeamState = useAdminTeamState({ setError, setSuccess });
  const {
    teamMembers,
    loadingTeam,
    showAddTeamMember,
    showChangePassword,
    showResetPassword,
    newTeamMember,
    passwordData,
    resetPasswordData,
    setShowAddTeamMember,
    setShowChangePassword,
    setShowResetPassword,
    setNewTeamMember,
    setPasswordData,
    setResetPasswordData,
    fetchTeamMembers,
    handleAddTeamMember,
    handleRemoveTeamMember,
    handleChangePassword,
    handleResetPassword,
    setTeamMembers,
  } = adminTeamState;

  // Handler for permissions update from TeamSection
  const handlePermissionsUpdate = (updatedMember) => {
    setTeamMembers(prev => 
      prev.map(m => m.id === updatedMember.id ? updatedMember : m)
    );
    setSuccess(`Permissions updated for ${updatedMember.name}`);
  };
  
  // Clubhouse admin state - extracted to useClubhouseState hook
  const clubhouseState = useClubhouseState({ setError, setSuccess });
  const {
    clubhouseMembers,
    loadingClubhouse,
    clubhouseView,
    monthlyLeaderboard,
    quarterlyLeaderboard,
    winners,
    schedulerStatus,
    memberSubscriptionFilter,
    memberStatusFilter,
    deleteMemberModal,
    setClubhouseView,
    setMemberSubscriptionFilter,
    setMemberStatusFilter,
    setDeleteMemberModal,
    fetchClubhouseData,
    openDeleteMemberModal,
    closeDeleteMemberModal,
    handleDeleteMemberStep,
    confirmDeleteMember,
    exportClubhouseMembers,
    exportWinners,
  } = clubhouseState;
  
  // Giveaways CMS state - extracted to useGiveawaysState hook
  const giveawaysState = useGiveawaysState({ setError, setSuccess });
  const {
    giveaways,
    loadingGiveaways,
    selectedGiveaway,
    showGiveawayEditor,
    giveawayForm,
    setGiveaways,
    setSelectedGiveaway,
    setShowGiveawayEditor,
    setGiveawayForm,
    fetchGiveaways,
    handleCreateGiveaway,
    handleUpdateGiveaway,
    handleActivateGiveaway,
    handleDeactivateGiveaway,
    handleDeleteGiveaway,
    openGiveawayEditor,
    handleGiveawayFieldChange,
    resetGiveawayForm,
  } = giveawaysState;
  
  // Homepage Layout state - extracted to useHomepageLayoutState hook
  const homepageLayoutState = useHomepageLayoutState({ setError, setSuccess });
  const {
    homepageLayout,
    loadingLayout,
    editingLayout,
    layoutHasChanges,
    draggedSectionIndex,
    setHomepageLayout,
    setEditingLayout,
    setLayoutHasChanges,
    setDraggedSectionIndex,
    fetchHomepageLayout,
    handleLayoutSectionMove,
    handleLayoutSectionToggle,
    handleLayoutSectionColor,
    handleLayoutSave,
    handleLayoutReset,
    handleLayoutCancel,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = homepageLayoutState;
  
  // Email Templates state (Welcome Email) - extracted to useEmailTemplatesState hook
  const emailTemplatesState = useEmailTemplatesState({ setError, setSuccess });
  const {
    welcomeEmailSettings,
    loadingEmailSettings,
    emailSettingsHasChanges,
    sendingTestEmail,
    testEmailRecipient,
    emailPreviewHtml,
    setWelcomeEmailSettings,
    setEmailSettingsHasChanges,
    setTestEmailRecipient,
    setEmailPreviewHtml,
    fetchWelcomeEmailSettings,
    handleWelcomeEmailFieldChange,
    handleWelcomeEmailSocialLinkChange,
    handleAddWelcomeEmailSocialLink,
    handleRemoveWelcomeEmailSocialLink,
    handleSaveWelcomeEmailSettings,
    handleSendTestEmail,
    fetchEmailPreview,
  } = emailTemplatesState;
  
  // Email Campaigns V2 state - enhanced features (Email Studio)
  const campaignsV2State = useCampaignsV2State({ setError, setSuccess });
  const {
    campaignsV2,
    loadingCampaignsV2,
    selectedCampaignId,
    campaignV2View,
    sendingCampaignV2,
    sendConfirmPhrase,
    setCampaignV2View,
    setSendConfirmPhrase,
    loadCampaignsV2Data,
    handleCreateCampaignV2,
    handleEditCampaignV2,
    handleDeleteCampaignV2,
    handleReviewCampaignV2,
    handleSendCampaignV2,
    handleBackToList,
    handleViewAnalyticsV2,
  } = campaignsV2State;
  
  // Episodes management state - extracted to useEpisodesState hook
  const episodesState = useEpisodesState({ setError, setSuccess });
  const {
    cmsEpisodes,
    loadingEpisodes,
    editingEpisode,
    showEpisodeModal,
    episodeModalMode,
    episodeMediaList,
    episodeMediaFilter,
    episodeMediaLoading,
    episodeSelectedMedia,
    setCmsEpisodes,
    setEditingEpisode,
    setShowEpisodeModal,
    setEpisodeModalMode,
    setEpisodeMediaList,
    setEpisodeMediaFilter,
    setEpisodeMediaLoading,
    setEpisodeSelectedMedia,
    fetchCmsEpisodes,
    handleSetFeaturedHero,
    handleRemoveFeaturedHero,
    handleCreateEpisode,
    handleEditEpisode,
    handleDeleteEpisode,
    handleDuplicateEpisode,
    handleSaveEpisode,
    handleEpisodeFieldChange,
    handleOpenEpisodeMediaPicker,
    handleEpisodeMediaSelect,
    handleBackToEpisodeEdit,
    getFilteredEpisodeMedia,
    handleToggleHidden,
  } = episodesState;
  
  // Link Hub state - extracted to useLinkHubState hook
  const linkHubState = useLinkHubState({ setError, setSuccess });
  const {
    linkHubPages,
    loadingLinkHub,
    editingLinkHub,
    showLinkHubModal,
    linkHubModalTab,
    linkHubModalView,
    globalSocialLinks,
    setLinkHubPages,
    setEditingLinkHub,
    setShowLinkHubModal,
    setLinkHubModalTab,
    setLinkHubModalView,
    setGlobalSocialLinks,
    fetchLinkHubPages,
    fetchGlobalSocialLinks,
    handleCreateLinkHub,
    handleEditLinkHub,
    handleSaveLinkHub,
    handleDeleteLinkHub,
    handleLinkHubFieldChange,
    handleAddLink,
    handleUpdateLink,
    handleRemoveLink,
    handleMoveLink,
    handleAddSocialLink,
    handleUpdateSocialLink,
    handleRemoveSocialLink,
    handleToggleDefaultSocial,
    isDefaultSocialEnabled,
    handleLinkHubThemeChange,
    applyThemePreset,
  } = linkHubState;

  // Guests management state - extracted to useGuestsState hook
  const guestsState = useGuestsState({ setError, setSuccess });
  const {
    cmsGuests,
    loadingGuests,
    editingGuest,
    showGuestModal,
    guestModalView,
    guestSearchQuery,
    newGuestData,
    setCmsGuests,
    setEditingGuest,
    setShowGuestModal,
    setGuestModalView,
    setGuestSearchQuery,
    setNewGuestData,
    fetchCmsGuests,
    handleCreateGuest,
    handleEditGuest,
    handleSaveGuest,
    handleDeleteGuest,
    handleToggleHiddenGuest,
    handleGuestFieldChange,
    handleOpenInlineAddGuest,
    handleBackFromAddGuest,
    handleNestedGuestFieldChange,
    handleSaveInlineGuest,
  } = guestsState;
  
  // Series management state - extracted to useSeriesState hook
  const seriesState = useSeriesState({ setError, setSuccess });
  const {
    cmsSeries,
    loadingSeries,
    editingSeries,
    showSeriesModal,
    setEditingSeries,
    setShowSeriesModal,
    fetchCmsSeries,
    handleCreateSeries,
    handleEditSeries,
    handleSaveSeries,
    handleDeleteSeries,
    handleSeriesFieldChange,
  } = seriesState;
  
  // Member Vault (Perks) management state - extracted to useMemberVaultState hook
  const memberVaultState = useMemberVaultState({ setError, setSuccess });
  const {
    perks,
    filteredPerks,
    loadingPerks,
    editingPerk,
    showPerkModal,
    categoryFilter,
    perkCategories,
    sponsorLogos,
    loadingSponsorLogos,
    editingSponsorLogo,
    showSponsorLogoModal,
    lockerTab,
    setPerks,
    setEditingPerk,
    setShowPerkModal,
    setSponsorLogos,
    setEditingSponsorLogo,
    setShowSponsorLogoModal,
    setLockerTab,
    setCategoryFilter,
    fetchPerks,
    fetchPerkCategories,
    handleCreatePerk,
    handleEditPerk,
    handleSavePerk,
    handleDeletePerk,
    handleTogglePerkActive,
    handleTogglePerkFeatured,
    handleNotifyMembers,
    fetchSponsorLogos,
    handleCreateSponsorLogo,
    handleEditSponsorLogo,
    handleSaveSponsorLogo,
    handleDeleteSponsorLogo,
  } = memberVaultState;

  // Wrapper functions for inline guest handlers (used by EpisodeEditorModal)
  // These bind the required dependencies that the hook's functions expect
  const wrappedHandleOpenInlineAddGuest = () => handleOpenInlineAddGuest(setEpisodeModalMode);
  const wrappedHandleBackFromAddGuest = () => handleBackFromAddGuest(setEpisodeModalMode);
  const wrappedHandleSaveInlineGuest = () => handleSaveInlineGuest(editingEpisode, handleEpisodeFieldChange, setEpisodeModalMode);

  // Media Picker state - extracted to useMediaPickerState hook
  const mediaPickerState = useMediaPickerState({
    setEditingGuest,
    setEditingEpisode,
    setSiteSettings,
    siteSettings,
    setWelcomeEmailSettings,
    selectedPage,
    setSelectedPage,
    updateBlockContent,
  });
  const {
    showMediaPicker,
    mediaPickerConfig,
    setShowMediaPicker,
    setMediaPickerConfig,
    handleOpenMediaPicker,
    handleOpenLogoMediaPicker,
    handleOpenWelcomeEmailMediaPicker,
    closeMediaPicker,
    handleMediaSelect,
  } = mediaPickerState;

  // Verify JWT token on mount
  useEffect(() => {
    const verifyAuth = async () => {
      const token = sessionStorage.getItem('golfnuts_admin_token');
      const isAuthenticated = sessionStorage.getItem('golfnuts_admin_auth');
      
      if (!token || !isAuthenticated) {
        const currentTab = searchParams.get('tab');
        navigate(currentTab ? `/admin/login?returnTab=${currentTab}` : '/admin/login');
        return;
      }

      try {
        // Verify token with backend
        const response = await axios.get(`${API_URL}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.valid) {
          setAdminUser(response.data.admin);
        } else {
          // Token invalid, redirect to login
          sessionStorage.removeItem('golfnuts_admin_token');
          sessionStorage.removeItem('golfnuts_admin_auth');
          sessionStorage.removeItem('golfnuts_admin_user');
          const currentTab = searchParams.get('tab');
          navigate(currentTab ? `/admin/login?returnTab=${currentTab}` : '/admin/login');
        }
      } catch (err) {
        console.error('Auth verification failed:', err);
        // Token expired or invalid
        sessionStorage.removeItem('golfnuts_admin_token');
        sessionStorage.removeItem('golfnuts_admin_auth');
        sessionStorage.removeItem('golfnuts_admin_user');
        const currentTab = searchParams.get('tab');
        navigate(currentTab ? `/admin/login?returnTab=${currentTab}` : '/admin/login');
      }
    };

    verifyAuth();
  }, [navigate]);

  // Auto-dismiss success and error messages after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000); // Errors stay a bit longer
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (activeTab === 'pages') {
      fetchPages();
    } else if (activeTab === 'settings') {
      fetchSiteSettings();
    } else if (activeTab === 'members') {
      fetchMembers();
    } else if (activeTab === 'clubhouse') {
      fetchClubhouseData();
    } else if (activeTab === 'team') {
      fetchTeamMembers();
    } else if (activeTab === 'episodes') {
      fetchCmsEpisodes();
      fetchCmsGuests(); // Also fetch guests for episode tagging
      fetchCmsSeries(); // Also fetch series for episode dropdown
    } else if (activeTab === 'linkhub') {
      fetchLinkHubPages();
    } else if (activeTab === 'guests') {
      fetchCmsGuests();
    } else if (activeTab === 'series') {
      fetchCmsSeries();
    } else if (activeTab === 'giveaways') {
      fetchGiveaways();
    } else if (activeTab === 'homepage-layout') {
      fetchHomepageLayout();
    } else if (activeTab === 'email-templates') {
      fetchWelcomeEmailSettings();
    } else if (activeTab === 'email-studio') {
      loadCampaignsV2Data();
    } else if (activeTab === 'member-locker') {
      fetchPerks();
      fetchSponsorLogos();
      fetchPerkCategories();
    }
    // modals tab is self-contained, no fetch needed here
  }, [activeTab, clubhouseView, memberStatusFilter]);
  
  // Email Templates management functions - MOVED TO useEmailTemplatesState hook
  // See /app/frontend/src/hooks/admin/useEmailTemplatesState.js

  // Email Campaigns V2 management functions - MOVED TO useCampaignsV2State hook
  // See /app/frontend/src/hooks/admin/useCampaignsV2State.js

  // Episodes management functions - MOVED TO useEpisodesState hook
  // See /app/frontend/src/hooks/admin/useEpisodesState.js

  // Link Hub management functions - MOVED TO useLinkHubState hook
  // See /app/frontend/src/hooks/admin/useLinkHubState.js

  // Guests management functions - MOVED TO useGuestsState hook
  // See /app/frontend/src/hooks/admin/useGuestsState.js

  // Series management functions - MOVED TO useSeriesState hook
  // See /app/frontend/src/hooks/admin/useSeriesState.js

  // Member Vault (Perks) management functions - MOVED TO useMemberVaultState hook
  // See /app/frontend/src/hooks/admin/useMemberVaultState.js

  // Giveaways management functions - MOVED TO useGiveawaysState hook
  // See /app/frontend/src/hooks/admin/useGiveawaysState.js

  // Homepage Layout management functions - MOVED TO useHomepageLayoutState hook
  // See /app/frontend/src/hooks/admin/useHomepageLayoutState.js

  // Team management functions - MOVED TO useAdminTeamState hook
  // See /app/frontend/src/hooks/admin/useAdminTeamState.js

  // Clubhouse management functions - MOVED TO useClubhouseState hook
  // See /app/frontend/src/hooks/admin/useClubhouseState.js

  // Members management functions - MOVED TO useMembersState hook
  // See /app/frontend/src/hooks/admin/useMembersState.js

  // Pages management functions - MOVED TO usePagesState hook
  // See /app/frontend/src/hooks/admin/usePagesState.js

  // Site Settings management functions - MOVED TO useSettingsState hook
  // See /app/frontend/src/hooks/admin/useSettingsState.js

  // Media Picker functions - MOVED TO useMediaPickerState hook
  // See /app/frontend/src/hooks/admin/useMediaPickerState.js

  const handleAddBlock = async (sectionId, blockType) => {
    try {
      const newBlock = {
        type: blockType,
        content: getDefaultBlockContent(blockType),
        isVisible: true
      };
      
      const response = await axios.post(`${API_URL}/api/cms/sections/${sectionId}/blocks`, newBlock);
      
      // Refresh page details
      if (selectedPage) {
        fetchPageDetails(selectedPage.id);
      }
      setSuccess('Block added successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to add block: ' + err.message);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Are you sure you want to delete this block?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/cms/blocks/${blockId}`);
      
      // Refresh page details
      if (selectedPage) {
        fetchPageDetails(selectedPage.id);
      }
      setSuccess('Block deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete block: ' + err.message);
    }
  };

  const getDefaultBlockContent = (blockType) => {
    switch(blockType) {
      case 'HOST_CARD':
        return {
          name: 'New Host',
          role: 'Host',
          bio: 'Host bio...',
          imageUrl: '',
          socialLinks: {},
          isActive: true
        };
      case 'EPISODE_CARD':
        return {
          title: 'New Episode',
          description: 'Episode description...',
          imageUrl: '',
          publishDate: new Date().toISOString(),
          duration: '45 min',
          series: 'Main Show',
          slug: 'new-episode',
          youtubeId: '',
          showComingSoon: false
        };
      case 'BUTTON':
        return {
          text: 'New Button',
          linkType: 'internal',
          internalPage: '/',
          externalUrl: '',
          style: 'primary',
          openInNewTab: false
        };
      case 'TEXT':
        return {
          title: 'New Section',
          description: 'Description...'
        };
      default:
        return {};
    }
  };

  const availablePages = [
    { value: '/', label: 'Home' },
    { value: '/episodes', label: 'Episodes' },
    { value: '/hosts', label: 'Hosts' },
    { value: '/guests', label: 'Guests' },
    { value: '/about', label: 'About' },
    { value: '/contact', label: 'Contact' }
  ];

  // Nested Media Selector Component for Link Hub Modal
  const LinkHubMediaSelector = ({ onSelect, onBack }) => {
    const [media, setMedia] = useState([]);
    const [filteredMedia, setFilteredMedia] = useState([]);
    const [filter, setFilter] = useState('all');
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
      const fetchMedia = async () => {
        setLoadingMedia(true);
        try {
          const response = await axios.get(`${API_URL}/api/cms/media`);
          setMedia(response.data);
          setFilteredMedia(response.data);
        } catch (err) {
          console.error('Error fetching media:', err);
        } finally {
          setLoadingMedia(false);
        }
      };
      fetchMedia();
    }, []);

    useEffect(() => {
      if (filter === 'all') {
        setFilteredMedia(media.filter(m => m.type === 'image'));
      } else {
        setFilteredMedia(media.filter(m => m.type === 'image' && m.category === filter));
      }
    }, [filter, media]);

    const categories = [
      { value: 'all', label: 'All Images' },
      { value: 'logo', label: 'Logos' },
      { value: 'host-photo', label: 'Host Photos' },
      { value: 'guest-placeholder', label: 'Guest Photos' }
    ];

    return (
      <>
        <div className="episode-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button 
              className="btn-back-nested" 
              onClick={onBack}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#64748b'
              }}
            >
              <ChevronLeft size={24} />
            </button>
            <h3>Select Profile Photo</h3>
          </div>
          <button className="modal-close-btn" onClick={onBack}>
            <X size={20} />
          </button>
        </div>
        
        <div className="episode-modal-body" style={{ padding: '0' }}>
          {/* Filter tabs */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            flexWrap: 'wrap'
          }}>
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setFilter(cat.value)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: filter === cat.value ? '#004534' : '#f1f5f9',
                  color: filter === cat.value ? 'white' : '#64748b',
                  transition: 'all 0.2s ease'
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
          
          {/* Media grid */}
          <div style={{ 
            padding: '16px 24px',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {loadingMedia ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                Loading media...
              </div>
            ) : filteredMedia.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                No images found in this category.
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '12px'
              }}>
                {filteredMedia.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: selectedItem?.id === item.id ? '3px solid #004534' : '3px solid transparent',
                      transition: 'all 0.2s ease',
                      background: '#f8fafc'
                    }}
                  >
                    <div style={{ 
                      aspectRatio: '1',
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={item.thumbnail || item.url} 
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                    <div style={{
                      padding: '8px',
                      fontSize: '12px',
                      color: '#64748b',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {item.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="episode-modal-footer">
          <button className="btn-cancel" onClick={onBack}>
            Back
          </button>
          <button 
            className="btn-save" 
            onClick={() => selectedItem && onSelect(selectedItem)}
            disabled={!selectedItem}
            style={{ opacity: selectedItem ? 1 : 0.5 }}
          >
            <ImageIcon size={18} />
            Select Image
          </button>
        </div>
      </>
    );
  };

  return (
    <div className={`admin-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Mobile Menu - Extracted Component */}
      <AdminMobileMenu 
        sidebarOpen={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)} 
      />
      
      {/* Sidebar - Extracted Component */}
      <AdminSidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        sidebarOpen={sidebarOpen}
        adminUser={adminUser}
        onAccessDenied={(tabId, tabName) => {
          setError(`Access Denied: You don't have permission to access ${tabName}. Contact a super admin to request access.`);
        }}
      />

      {/* Main Content */}
      <main className="admin-main">
        {/* Notifications */}
        {error && (
          <div className="admin-alert admin-alert-error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        {success && (
          <div className="admin-alert admin-alert-success">
            {success}
            <button onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        {/* Read-only mode banner */}
        {isReadOnly && (
          <div className="admin-alert admin-alert-readonly">
            <span className="readonly-icon">👁</span>
            <span>You are viewing this section in <strong>Read-Only</strong> mode. Contact a super admin to request write access.</span>
          </div>
        )}

        {loading && <div className="admin-loading">Loading...</div>}

        {/* Suspense wrapper for lazy-loaded sections */}
        <Suspense fallback={<SectionLoadingFallback />}>
          {/* Pages Tab - NEW PageManagementV2 Component */}
          {activeTab === 'pages' && (
            <PageManagementV2
              setShowMediaPicker={setShowMediaPicker}
              setMediaPickerConfig={setMediaPickerConfig}
              setError={setError}
              setSuccess={setSuccess}
              onTabChange={handleTabChange}
            />
          )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="admin-content">
            <MediaLibrary isReadOnly={isReadOnly} />
          </div>
        )}

        {/* Settings Tab */}
        {/* Settings Tab - Extracted Component */}
        {activeTab === 'settings' && siteSettings && (
          <SettingsSection
            siteSettings={siteSettings}
            selectedSettingsTab={selectedSettingsTab}
            loading={loading}
            setSelectedSettingsTab={setSelectedSettingsTab}
            handleSettingsChange={handleSettingsChange}
            handleSaveSettings={handleSaveSettings}
            handleOpenLogoMediaPicker={handleOpenLogoMediaPicker}
          />
        )}

        {/* Members Tab (Newsletter Signups) - Extracted Component */}
        {activeTab === 'members' && (
          <MembersSection
            members={members}
            loadingMembers={loadingMembers}
            fetchMembers={fetchMembers}
            exportMembers={exportMembers}
            deleteMember={deleteMember}
          />
        )}

        {/* Clubhouse Tab - Extracted Component */}
        {activeTab === 'clubhouse' && (
          <ClubhouseSection
            clubhouseMembers={clubhouseMembers}
            loadingClubhouse={loadingClubhouse}
            clubhouseView={clubhouseView}
            monthlyLeaderboard={monthlyLeaderboard}
            quarterlyLeaderboard={quarterlyLeaderboard}
            winners={winners}
            schedulerStatus={schedulerStatus}
            memberSubscriptionFilter={memberSubscriptionFilter}
            memberStatusFilter={memberStatusFilter}
            setClubhouseView={setClubhouseView}
            setMemberSubscriptionFilter={setMemberSubscriptionFilter}
            setMemberStatusFilter={setMemberStatusFilter}
            fetchClubhouseData={fetchClubhouseData}
            exportClubhouseMembers={exportClubhouseMembers}
            exportWinners={exportWinners}
            openDeleteMemberModal={openDeleteMemberModal}
          />
        )}

        {/* Member Modals Tab */}
        {activeTab === 'modals' && (
          <div className="admin-content">
            <AdminModals />
          </div>
        )}

        {/* Member Vault (Perks) Tab - Extracted Component */}
        {activeTab === 'member-locker' && (
          <MemberVaultSection
            lockerTab={lockerTab}
            perks={perks}
            filteredPerks={filteredPerks}
            categoryFilter={categoryFilter}
            perkCategories={perkCategories}
            sponsorLogos={sponsorLogos}
            loadingPerks={loadingPerks}
            loadingSponsorLogos={loadingSponsorLogos}
            editingPerk={editingPerk}
            editingSponsorLogo={editingSponsorLogo}
            showPerkModal={showPerkModal}
            showSponsorLogoModal={showSponsorLogoModal}
            setLockerTab={setLockerTab}
            setCategoryFilter={setCategoryFilter}
            setEditingPerk={setEditingPerk}
            setEditingSponsorLogo={setEditingSponsorLogo}
            setShowPerkModal={setShowPerkModal}
            setShowSponsorLogoModal={setShowSponsorLogoModal}
            handleCreatePerk={handleCreatePerk}
            handleEditPerk={handleEditPerk}
            handleSavePerk={handleSavePerk}
            handleDeletePerk={handleDeletePerk}
            handleTogglePerkActive={handleTogglePerkActive}
            handleTogglePerkFeatured={handleTogglePerkFeatured}
            handleNotifyMembers={handleNotifyMembers}
            handleCreateSponsorLogo={handleCreateSponsorLogo}
            handleEditSponsorLogo={handleEditSponsorLogo}
            handleSaveSponsorLogo={handleSaveSponsorLogo}
            handleDeleteSponsorLogo={handleDeleteSponsorLogo}
            setMediaPickerConfig={setMediaPickerConfig}
            setShowMediaPicker={setShowMediaPicker}
          />
        )}

        {/* Admin Team Management Tab - Extracted Component */}
        {activeTab === 'team' && (
          <TeamSection
            teamMembers={teamMembers}
            loadingTeam={loadingTeam}
            adminUser={adminUser}
            showAddTeamMember={showAddTeamMember}
            showChangePassword={showChangePassword}
            showResetPassword={showResetPassword}
            newTeamMember={newTeamMember}
            passwordData={passwordData}
            resetPasswordData={resetPasswordData}
            setShowAddTeamMember={setShowAddTeamMember}
            setShowChangePassword={setShowChangePassword}
            setShowResetPassword={setShowResetPassword}
            setNewTeamMember={setNewTeamMember}
            setPasswordData={setPasswordData}
            setResetPasswordData={setResetPasswordData}
            handleAddTeamMember={handleAddTeamMember}
            handleRemoveTeamMember={handleRemoveTeamMember}
            handleChangePassword={handleChangePassword}
            handleResetPassword={handleResetPassword}
            onPermissionsUpdate={handlePermissionsUpdate}
          />
        )}

        {/* Episodes Tab */}
        {/* Episodes Tab - Extracted Component */}
        {activeTab === 'episodes' && (
          <EpisodesSection
            cmsEpisodes={cmsEpisodes}
            loadingEpisodes={loadingEpisodes}
            handleCreateEpisode={handleCreateEpisode}
            fetchCmsEpisodes={fetchCmsEpisodes}
            handleSetFeaturedHero={handleSetFeaturedHero}
            handleRemoveFeaturedHero={handleRemoveFeaturedHero}
            handleEditEpisode={handleEditEpisode}
            handleDuplicateEpisode={handleDuplicateEpisode}
            handleDeleteEpisode={handleDeleteEpisode}
            handleToggleHidden={handleToggleHidden}
            isReadOnly={isReadOnly}
          />
        )}

        {/* Guests Tab */}
        {/* Guests Tab - Extracted Component */}
        {activeTab === 'guests' && (
          <GuestsSection
            cmsGuests={cmsGuests}
            loadingGuests={loadingGuests}
            showGuestModal={showGuestModal}
            editingGuest={editingGuest}
            guestModalView={guestModalView}
            setShowGuestModal={setShowGuestModal}
            setGuestModalView={setGuestModalView}
            handleCreateGuest={handleCreateGuest}
            handleEditGuest={handleEditGuest}
            handleDeleteGuest={handleDeleteGuest}
            handleToggleHiddenGuest={handleToggleHiddenGuest}
            handleSaveGuest={handleSaveGuest}
            handleGuestFieldChange={handleGuestFieldChange}
            fetchCmsGuests={fetchCmsGuests}
            isReadOnly={isReadOnly}
          />
        )}

        {/* Series Tab - Extracted Component */}
        {activeTab === 'series' && (
          <SeriesSection
            cmsSeries={cmsSeries}
            loadingSeries={loadingSeries}
            showSeriesModal={showSeriesModal}
            editingSeries={editingSeries}
            setShowSeriesModal={setShowSeriesModal}
            handleCreateSeries={handleCreateSeries}
            handleEditSeries={handleEditSeries}
            handleDeleteSeries={handleDeleteSeries}
            handleSaveSeries={handleSaveSeries}
            handleSeriesFieldChange={handleSeriesFieldChange}
            fetchCmsSeries={fetchCmsSeries}
            isReadOnly={isReadOnly}
          />
        )}

        {/* Link Hub Tab - Extracted Component */}
        {activeTab === 'linkhub' && (
          <LinkHubSection
            linkHubPages={linkHubPages}
            loadingLinkHub={loadingLinkHub}
            handleCreateLinkHub={handleCreateLinkHub}
            fetchLinkHubPages={fetchLinkHubPages}
            handleEditLinkHub={handleEditLinkHub}
            handleDeleteLinkHub={handleDeleteLinkHub}
            isReadOnly={isReadOnly}
          />
        )}

        {/* Giveaways Tab */}
        {/* Giveaways Tab - Extracted Component */}
        {activeTab === 'giveaways' && (
          <GiveawaysSection
            giveaways={giveaways}
            loadingGiveaways={loadingGiveaways}
            openGiveawayEditor={openGiveawayEditor}
            fetchGiveaways={fetchGiveaways}
            handleActivateGiveaway={handleActivateGiveaway}
            handleDeactivateGiveaway={handleDeactivateGiveaway}
            handleDeleteGiveaway={handleDeleteGiveaway}
            formatDateWithoutOffset={formatDateWithoutOffset}
            isReadOnly={isReadOnly}
          />
        )}

        {/* Homepage Layout Tab - Extracted Component */}
        {activeTab === 'homepage-layout' && (
          <HomepageLayoutSection
            editingLayout={editingLayout}
            loadingLayout={loadingLayout}
            layoutHasChanges={layoutHasChanges}
            draggedSectionIndex={draggedSectionIndex}
            handleLayoutCancel={handleLayoutCancel}
            handleLayoutSave={handleLayoutSave}
            handleLayoutReset={handleLayoutReset}
            fetchHomepageLayout={fetchHomepageLayout}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            handleDragEnd={handleDragEnd}
            handleLayoutSectionMove={handleLayoutSectionMove}
            handleLayoutSectionColor={handleLayoutSectionColor}
            handleLayoutSectionToggle={handleLayoutSectionToggle}
            isReadOnly={isReadOnly}
          />
        )}

        {/* Welcome Email Tab */}
        {/* Welcome Email Tab - Extracted Component */}
        {activeTab === 'email-templates' && (
          <EmailTemplatesSection
            welcomeEmailSettings={welcomeEmailSettings}
            loadingEmailSettings={loadingEmailSettings}
            emailSettingsHasChanges={emailSettingsHasChanges}
            testEmailRecipient={testEmailRecipient}
            sendingTestEmail={sendingTestEmail}
            setTestEmailRecipient={setTestEmailRecipient}
            handleSaveWelcomeEmailSettings={handleSaveWelcomeEmailSettings}
            handleWelcomeEmailFieldChange={handleWelcomeEmailFieldChange}
            handleOpenWelcomeEmailMediaPicker={handleOpenWelcomeEmailMediaPicker}
            handleWelcomeEmailSocialLinkChange={handleWelcomeEmailSocialLinkChange}
            handleRemoveWelcomeEmailSocialLink={handleRemoveWelcomeEmailSocialLink}
            handleAddWelcomeEmailSocialLink={handleAddWelcomeEmailSocialLink}
            handleSendTestEmail={handleSendTestEmail}
          />
        )}

      {/* Link Hub Editor Modal */}
      {/* Link Hub Editor Modal */}
      {showLinkHubModal && editingLinkHub && (
        <LinkHubEditorModal
          editingLinkHub={editingLinkHub}
          linkHubModalView={linkHubModalView}
          linkHubModalTab={linkHubModalTab}
          globalSocialLinks={globalSocialLinks}
          setShowLinkHubModal={setShowLinkHubModal}
          setLinkHubModalView={setLinkHubModalView}
          setLinkHubModalTab={setLinkHubModalTab}
          setActiveTab={setActiveTab}
          setSelectedSettingsTab={setSelectedSettingsTab}
          handleLinkHubFieldChange={handleLinkHubFieldChange}
          handleLinkHubThemeChange={handleLinkHubThemeChange}
          handleAddLink={handleAddLink}
          handleUpdateLink={handleUpdateLink}
          handleMoveLink={handleMoveLink}
          handleRemoveLink={handleRemoveLink}
          handleAddSocialLink={handleAddSocialLink}
          handleUpdateSocialLink={handleUpdateSocialLink}
          handleRemoveSocialLink={handleRemoveSocialLink}
          isDefaultSocialEnabled={isDefaultSocialEnabled}
          handleToggleDefaultSocial={handleToggleDefaultSocial}
          applyThemePreset={applyThemePreset}
          handleSaveLinkHub={handleSaveLinkHub}
          LinkHubMediaSelectorComponent={LinkHubMediaSelector}
        />
      )}

      {/* Email Studio Tab */}
      {activeTab === 'email-studio' && (
        <EmailCampaignsV2Section
          // State
          campaignsV2={campaignsV2}
          loadingCampaignsV2={loadingCampaignsV2}
          selectedCampaignId={selectedCampaignId}
          campaignV2View={campaignV2View}
          sendingCampaignV2={sendingCampaignV2}
          sendConfirmPhrase={sendConfirmPhrase}
          // Setters
          setCampaignV2View={setCampaignV2View}
          setSendConfirmPhrase={setSendConfirmPhrase}
          // Actions
          loadCampaignsV2Data={loadCampaignsV2Data}
          handleCreateCampaignV2={handleCreateCampaignV2}
          handleEditCampaignV2={handleEditCampaignV2}
          handleDeleteCampaignV2={handleDeleteCampaignV2}
          handleReviewCampaignV2={handleReviewCampaignV2}
          handleSendCampaignV2={handleSendCampaignV2}
          handleViewAnalyticsV2={handleViewAnalyticsV2}
          handleBackToList={handleBackToList}
          // Media picker
          setShowMediaPicker={setShowMediaPicker}
          setMediaPickerConfig={setMediaPickerConfig}
        />
      )}
        </Suspense>
      </main>

      {/* Giveaway Editor Modal */}
      <Suspense fallback={<SectionLoadingFallback />}>
        {showGiveawayEditor && (
        <GiveawayEditorModal
          selectedGiveaway={selectedGiveaway}
          giveawayForm={giveawayForm}
          setShowGiveawayEditor={setShowGiveawayEditor}
          setShowMediaPicker={setShowMediaPicker}
          setMediaPickerConfig={setMediaPickerConfig}
          handleGiveawayFieldChange={handleGiveawayFieldChange}
          handleCreateGiveaway={handleCreateGiveaway}
          handleUpdateGiveaway={handleUpdateGiveaway}
        />
      )}

      {/* Episode Edit Modal      {/* Episode Edit Modal */}
      {showEpisodeModal && editingEpisode && (
        <EpisodeEditorModal
          editingEpisode={editingEpisode}
          episodeModalMode={episodeModalMode}
          cmsSeries={cmsSeries}
          cmsGuests={cmsGuests}
          guestSearchQuery={guestSearchQuery}
          episodeMediaFilter={episodeMediaFilter}
          episodeMediaLoading={episodeMediaLoading}
          episodeSelectedMedia={episodeSelectedMedia}
          newGuestData={newGuestData}
          setShowEpisodeModal={setShowEpisodeModal}
          setEpisodeModalMode={setEpisodeModalMode}
          setGuestSearchQuery={setGuestSearchQuery}
          setEpisodeMediaFilter={setEpisodeMediaFilter}
          setEpisodeSelectedMedia={setEpisodeSelectedMedia}
          handleEpisodeFieldChange={handleEpisodeFieldChange}
          handleOpenEpisodeMediaPicker={handleOpenEpisodeMediaPicker}
          handleBackToEpisodeEdit={handleBackToEpisodeEdit}
          handleOpenInlineAddGuest={wrappedHandleOpenInlineAddGuest}
          handleSaveEpisode={handleSaveEpisode}
          getFilteredEpisodeMedia={getFilteredEpisodeMedia}
          handleEpisodeMediaSelect={handleEpisodeMediaSelect}
          handleBackFromAddGuest={wrappedHandleBackFromAddGuest}
          handleNestedGuestFieldChange={handleNestedGuestFieldChange}
          handleSaveInlineGuest={wrappedHandleSaveInlineGuest}
        />
      )}

      {/* Delete Member Modal */}
      {deleteMemberModal.show && (
        <DeleteMemberModal
          deleteMemberModal={deleteMemberModal}
          setDeleteMemberModal={setDeleteMemberModal}
          closeDeleteMemberModal={closeDeleteMemberModal}
          handleDeleteMemberStep={handleDeleteMemberStep}
          confirmDeleteMember={confirmDeleteMember}
        />
      )}
      </Suspense>

      {/* Media Picker Modal - MUST BE LAST to ensure proper z-index stacking
          This modal needs to appear ABOVE all other modals when triggered from
          nested contexts (e.g., from within Giveaway editor, Welcome Email settings, etc.)
      */}
      <Suspense fallback={<SectionLoadingFallback />}>
        {showMediaPicker && (
          <MediaPicker
            onSelect={handleMediaSelect}
            onCancel={() => {
              setShowMediaPicker(false);
              setMediaPickerConfig(null);
            }}
          />
        )}
      </Suspense>
    </div>
  );
}

export default Admin;